import { Action, createReducer, on } from '@ngrx/store';
import { CandidateInfo } from 'src/app/esign/models/candidate-info.model';
import { RequiredDocument } from 'src/app/esign/models/required-document.model';
import { TemplateSearchParameters } from 'src/app/esign/models/template-search-parameters.model';
import { TemplateFolder } from 'src/app/esign/models/template-folder.model';
import { Template } from 'src/app/esign/models/template.model';
import { esignActions } from './actions';
import { ExistingEnvelope } from 'src/app/esign/models/existing-envelope.model';
import { PortalEnvelope } from 'src/app/esign/models/portal-envelope.model';
import { EsignPermissions } from 'src/app/esign/models/esign-permissions.model';
import { isEmpty } from 'src/app/shared/utilities/obj-utilities';

export const esignFeatureKey = 'esign';

export interface EsignState {
    candidateInfo: CandidateInfo;
    requiredDocuments: RequiredDocument[];
    templateFolders: TemplateFolder[];
    selectedFolderId: string;
    templateSearchResult: Template[];
    selectedDocumentType: RequiredDocument;
    templateSearchParameters: TemplateSearchParameters;
    envelopes: ExistingEnvelope[];
    portalEnvelopes: PortalEnvelope[];
    esignDocuments: RequiredDocument[];
    isCreateMode: boolean;
    error: any;
    isTemplateSearchComplete: boolean;
    esignPermissions: EsignPermissions;
}

export const initialState: EsignState = {
    candidateInfo: null,
    requiredDocuments: null,
    templateFolders: null,
    selectedFolderId: null,
    templateSearchResult: null,
    selectedDocumentType: null,
    templateSearchParameters: { searchText: null, searchAllFolders: true },
    envelopes: null,
    portalEnvelopes: null,
    esignDocuments: null,
    isCreateMode: null,
    error: null,
    isTemplateSearchComplete: null,
    esignPermissions: null
};

const esignReducer = createReducer(
    initialState,
    on(esignActions.EsignActions.getCandidateInfoSuccess, (state, action) => ({
        ...state,
        candidateInfo: action.candidateInfo,
        error: ''
    })),

    on(esignActions.EsignActions.getRequiredDocumentsSuccess, (state, action) => ({
        ...state,
        requiredDocuments: action.requiredDocuments,
        createMode: action.requiredDocuments.length > 0,
        error: ''
    })),
    on(esignActions.EsignActions.removeRequiredDocument, (state, action) => ({
        ...state,
        requiredDocuments: state.requiredDocuments.filter((r) => r.documentTypeId !== action.documentTypeId),
        error: ''
    })),
    on(esignActions.EsignActions.getTemplateFoldersSuccess, (state, action) => ({
        ...state,
        templateFolders: action.folders,
        error: ''
    })),
    on(esignActions.EsignActions.setSelectedFolder, (state, action) => ({
        ...state,
        selectedFolderId: action.folderId,
        error: ''
    })),
    on(esignActions.EsignActions.clearSelectedFolder, (state, action) => ({
        ...state,
        selectedFolderId: null,
        error: ''
    })),
    on(esignActions.EsignActions.searchTemplates, (state, action) => ({
        ...state,
        templateSearchParameters: { searchText: action.name, searchAllFolders: isEmpty(action.folderId) },
        error: ''
    })),
    on(esignActions.EsignActions.searchTemplatesSuccess, (state, action) => ({
        ...state,
        templateSearchResult: action.templates,
        error: ''
    })),
    on(esignActions.EsignActions.setSelectedDocumentType, (state, action) => ({
        ...state,
        selectedDocumentType: action.requirement,
        error: ''
    })),
    on(esignActions.EsignActions.clearTemplateMappingState, (state, action) => ({
        ...state,
        selectedDocumentType: null,
        templateSearchResult: null,
        selectedFolderId: null,
        templateSearchParameters: initialState.templateSearchParameters,
        error: ''
    })),
    on(esignActions.EsignActions.associateTemplateDocumentTypeSuccess, (state, action) => ({
        ...state,
        requiredDocuments: state.requiredDocuments.map((d) =>
            d.documentTypeId === action.documentTypeId
                ? {
                      ...d,
                      templateName: action.template.name,
                      externalTemplateId: action.template.id,
                      templatePageCount: action.template.pageCount,
                      internalTemplateId: action.templateId,
                      templateStateId: action.stateId
                  }
                : d
        ),
        error: ''
    })),
    on(esignActions.EsignActions.associateTemplateDocumentTypeAdHocSuccess, (state, action) => ({
        ...state,
        requiredDocuments: state.requiredDocuments.map((d) =>
            d.documentTypeId === action.documentTypeId
                ? {
                      ...d,
                      templateName: action.template.name,
                      externalTemplateId: action.template.id,
                      templatePageCount: action.template.pageCount,
                      internalTemplateId: action.templateId,
                      templateStateId: action.template.stateId
                  }
                : d
        )
    })),
    on(esignActions.EsignActions.disassociateTemplateDocumentTypeSuccess, (state, action) => ({
        ...state,
        requiredDocuments: state.requiredDocuments.map((d) =>
            d.documentTypeId === action.documentTypeId
                ? {
                      ...d,
                      externalTemplateId: null,
                      templateName: null,
                      templatePageCount: null,
                      showPreviewIcon: false
                  }
                : d
        ),
        error: ''
    })),
    on(esignActions.EsignActions.sendEnvelopeSuccess, (state, action) => ({
        ...state,
        requiredDocuments: [],
        createMode: false,
        error: ''
    })),
    on(esignActions.EsignActions.getTemplateImageSuccess, (state, action) => ({
        ...state,
        requiredDocuments:
            state.requiredDocuments?.map((d) => {
                if (action.templateName === d.templateName) {
                    return {
                        ...d,
                        showPreviewIcon: true
                    };
                }
                return d;
            }) ?? state.requiredDocuments,
        error: ''
    })),
    on(esignActions.EsignActions.getEnvelopeSuccess, (state, action) => ({
        ...state,
        envelopes: [action.envelope].concat(state.envelopes),
        error: ''
    })),
    on(esignActions.EsignActions.getContractEnvelopesSuccess, (state, action) => ({
        ...state,
        envelopes: action.envelopes,
        error: ''
    })),
    on(esignActions.EsignActions.getPortalEnvelopesSuccess, (state, action) => ({
        ...state,
        portalEnvelopes: action.envelopes,
        error: ''
    })),
    on(esignActions.EsignActions.getEsignDocumentsSuccess, (state, action) => ({
        ...state,
        esignDocuments: action.documents,
        requiredDocuments: state.requiredDocuments.map((d) => {
            const doc = action.documents.find((ad) => ad.documentTypeId === d.documentTypeId);
            return doc
                ? {
                      ...doc,
                      showPreviewIcon: doc.externalTemplateId === d.externalTemplateId ? d.showPreviewIcon : false
                  }
                : d;
        }),
        error: ''
    })),
    on(esignActions.EsignActions.addDocumentToEnvelope, (state, action) => ({
        ...state,
        requiredDocuments: [action.document].concat(state.requiredDocuments),
        error: ''
    })),
    on(esignActions.EsignActions.setCreateMode, (state, action) => ({
        ...state,
        isCreateMode: action.isCreateMode
    })),
    on(esignActions.EsignActions.setTemplateSearchComplete, (state, action) => ({
        ...state,
        isTemplateSearchComplete: action.isTemplateSearchComplete
    })),
    on(
        esignActions.EsignActions.getCandidateInfoFailure,
        esignActions.EsignActions.getTemplateFoldersFailure,
        esignActions.EsignActions.searchTemplatesFailure,
        esignActions.EsignActions.getRequiredDocumentsFailure,
        esignActions.EsignActions.getTemplateImageFailure,
        esignActions.EsignActions.disassociateTemplateDocumentTypeFailure,
        esignActions.EsignActions.sendEnvelopeFailure,
        esignActions.EsignActions.getEnvelopeFailure,
        esignActions.EsignActions.getContractEnvelopesFailure,
        esignActions.EsignActions.getPortalEnvelopesFailure,
        esignActions.EsignActions.getEsignDocumentsFailure,
        esignActions.EsignActions.cancelEnvelopeFailure,
        esignActions.EsignActions.associateTemplateDocumentTypeFailure,
        esignActions.EsignActions.associateTemplateDocumentTypeAdHocFailure,
        (state, action) => ({
            ...state,
            error: (action.error as any)?.message
        })
    ),
    on(esignActions.EsignActions.getEsignPermissionsSuccess, (state, action) => ({
        ...state,
        esignPermissions: action.esignPermissions,
        error: ''
    })),
    on(esignActions.EsignActions.getEsignPermissionsFailure, (state, action) => ({
        ...state,
        esignPermissions: null,
        error: (action.error as any)?.message
    }))
);

export function reducer(state: EsignState | undefined, action: Action) {
    return esignReducer(state, action);
}
